// ─────────────────────────────────────────────────────────────────────────────
// Per-module wiring for the /relatedlist widget.
//
// STANDARD modules each get their own clearly-separated block below, so one
// module can be changed / enabled without touching the others. Zoho-side, a
// module only shows the widget once its Related List is registered on the
// extension (Developer console → Components → Related Details). The code
// deliberately supports MORE modules than are currently registered
// (Accounts, Deals) — registering one of those later needs NO code change.
//
// Currently registered in the extension: Contacts, Sales_Orders, Products.
//
// CUSTOM modules are resolved through a SEPARATE path (see
// CUSTOM_MODULE_RESOURCES + resolveEntityConfig): any module not listed in
// ENTITY_REGISTRY renders via the Shopify resource declared on its Record
// View Setting (settingSchema.resource: "customers" | "orders" |
// "products"), configured from the Record View Setting tab. This keeps
// custom-module support fully separated from the standard registry.
// ─────────────────────────────────────────────────────────────────────────────

import Contact from "./Contact";
import Deal from "./Deal";
import Product from "./Product";
import {
  getShopifyCustomerById,
  getShopifyOrderById,
  getShopifyProductById,
} from "../../../APICalling/shopifyApi";

// ── Standard modules — one separated block per module ───────────────────────

// Contacts — Shopify CUSTOMER view (profile, orders, abandoned carts).
const CONTACTS_ENTRY = {
  Component: Contact,
  fetch: getShopifyCustomerById,
};

// Accounts — same Shopify CUSTOMER view as Contacts. Not registered on the
// extension today; kept so the client can register it later with no code
// change.
const ACCOUNTS_ENTRY = {
  Component: Contact,
  fetch: getShopifyCustomerById,
};

// Sales Orders — Shopify ORDER view (the default order sync target).
const SALES_ORDERS_ENTRY = {
  Component: Deal,
  fetch: getShopifyOrderById,
};

// Deals — Shopify ORDER view. Legacy order sync target (orders synced to
// Deals before the Sales_Orders switch). Not registered on the extension
// today; kept for legacy records and future re-registration.
const DEALS_ENTRY = {
  Component: Deal,
  fetch: getShopifyOrderById,
};

// Products — Shopify PRODUCT view (variants, inventory, KPIs).
const PRODUCTS_ENTRY = {
  Component: Product,
  fetch: getShopifyProductById,
};

export const ENTITY_REGISTRY = {
  Contacts: CONTACTS_ENTRY,
  Accounts: ACCOUNTS_ENTRY,
  Sales_Orders: SALES_ORDERS_ENTRY,
  Deals: DEALS_ENTRY,
  Products: PRODUCTS_ENTRY,
};

// ── Custom modules — separated resolution path ──────────────────────────────
// A custom module declares WHICH Shopify resource it mirrors via its Record
// View Setting (settingSchema.resource). The three renderers/fetchers are
// reused — no custom-module-specific UI needed.
export const CUSTOM_MODULE_RESOURCES = {
  customers: { Component: Contact, fetch: getShopifyCustomerById },
  orders: { Component: Deal, fetch: getShopifyOrderById },
  products: { Component: Product, fetch: getShopifyProductById },
};

/**
 * Resolve the widget config for a Zoho entity:
 *   1. Standard registry (Contacts / Accounts / Sales_Orders / Deals / Products).
 *   2. Custom module — via its relatedList setting's `resource`.
 * Returns null when the module is unsupported / not yet configured.
 */
export const resolveEntityConfig = (entity, settingSchema) => {
  if (!entity) return null;
  if (ENTITY_REGISTRY[entity]) return ENTITY_REGISTRY[entity];
  const resource = settingSchema?.resource;
  return (resource && CUSTOM_MODULE_RESOURCES[resource]) || null;
};
